import { create } from 'zustand';
import * as api from '../lib/api';
import type { Channel } from '../types';

// ThreadInfo is what the reply badge on a message needs, and nothing more. The
// thread's title is deliberately absent: ThreadPanel fetches its own when
// opened, so carrying one here would only be a second copy to keep in sync.
export interface ThreadInfo {
  id: string;
  message_id: string;
  parent_channel_id: string;
  message_count: number;
}

interface ThreadState {
  // Keyed by the origin message. A message has at most one thread, and every
  // read is "does this message have a thread, and how many replies" — so the
  // origin message is the natural key. Keying by channel instead would force
  // every consumer to rebuild this index, and every write to scan for it.
  byMessage: Record<string, ThreadInfo>;

  upsertThread: (thread: ThreadInfo) => void;
  clearChannel: (channelId: string) => void;
  purgeChannels: (channelIds: string[]) => void;
  reset: () => void;

  loadThreads: (channelId: string) => Promise<void>;
}

let threadsGeneration = 0;

export const useThreadStore = create<ThreadState>()((set) => ({
  byMessage: {},

  // One idempotent write covers creation, count changes, and replay of either.
  // There is no update-versus-insert branch to get wrong, and a frame that
  // arrives out of order costs nothing beyond a redundant assignment.
  upsertThread: (thread) =>
    set((s) => {
      const current = s.byMessage[thread.message_id];
      if (
        current &&
        current.id === thread.id &&
        current.message_count === thread.message_count &&
        current.parent_channel_id === thread.parent_channel_id
      ) {
        // Identity preserved on a no-op so subscribed rows do not re-render.
        return s;
      }
      return { byMessage: { ...s.byMessage, [thread.message_id]: thread } };
    }),

  clearChannel: (channelId) =>
    set((s) => {
      const next: Record<string, ThreadInfo> = {};
      let dropped = false;
      for (const [messageId, thread] of Object.entries(s.byMessage)) {
        if (thread.parent_channel_id === channelId) {
          dropped = true;
          continue;
        }
        next[messageId] = thread;
      }
      return dropped ? { byMessage: next } : s;
    }),

  purgeChannels: (channelIds) =>
    set((s) => {
      threadsGeneration++;
      const removed = new Set(channelIds);
      const byMessage = Object.fromEntries(
        Object.entries(s.byMessage).filter(
          ([, thread]) => !removed.has(thread.parent_channel_id) && !removed.has(thread.id),
        ),
      );
      return { byMessage };
    }),

  reset: () => {
    threadsGeneration++;
    set({ byMessage: {} });
  },

  loadThreads: async (channelId) => {
    const generation = threadsGeneration;
    try {
      const { threads } = await api.fetchActiveThreads(channelId);
      if (generation !== threadsGeneration) return;
      const fetched: ThreadInfo[] = threads
        .map((t: Channel & { message_id?: string; message_count?: number }) => ({
          id: t.id,
          message_id: t.message_id ?? '',
          parent_channel_id: channelId,
          message_count: t.message_count ?? 0,
        }))
        // A thread with no origin message has no badge to render, so it has no
        // place in a store keyed by origin message.
        .filter((t) => t.message_id);
      set((s) => {
        // This channel's slice is authoritative: entries it no longer lists are
        // gone, entries elsewhere are untouched.
        const next: Record<string, ThreadInfo> = {};
        for (const [messageId, thread] of Object.entries(s.byMessage)) {
          if (thread.parent_channel_id !== channelId) next[messageId] = thread;
        }
        for (const thread of fetched) {
          const current = s.byMessage[thread.message_id];
          // Reuse the stored object when nothing changed so subscribed rows do
          // not re-render on a refetch that found the same state.
          next[thread.message_id] =
            current &&
            current.id === thread.id &&
            current.message_count === thread.message_count &&
            current.parent_channel_id === thread.parent_channel_id
              ? current
              : thread;
        }
        return { byMessage: next };
      });
    } catch {
      // A failed refresh leaves the previous snapshot in place.
    }
  },
}));
